// MarketPulseEditor.js
import React, { useEffect, useMemo, useState } from "react";
import "./TradesDayHourChart.css"; // reuse the aiStatsChart styles (tdh-*, mp-label, etc.)
import CountsRatiosCell from "./CountsRatiosCell"; // adjust path

const DIRECTIONS = [
  "bullish",
  "bearish",
  "distribution",
  "recovery",
  "neutral",
  "caution",
  "whipsaw",
];

function titleCase(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return "—";
  return `${Number(n).toFixed(0)}%`;
}
function fmtDateISO(d) {
  try {
    const dt = typeof d === "string" ? new Date(d) : d;
    return dt.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}
function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return "—";
  return dt.toLocaleString();
}

function getHigherHistoricalProbability(row) {
  const longRaw = row?.historicalPrediction?.longProbabilityPct;
  const shortRaw = row?.historicalPrediction?.shortProbabilityPct;

  const longProbability =
    longRaw == null || !Number.isFinite(Number(longRaw))
      ? null
      : Number(longRaw);

  const shortProbability =
    shortRaw == null || !Number.isFinite(Number(shortRaw))
      ? null
      : Number(shortRaw);

  if (longProbability == null && shortProbability == null) {
    return null;
  }

  if (
    longProbability != null &&
    (shortProbability == null || longProbability > shortProbability)
  ) {
    return {
      side: "LONG",
      probability: longProbability,
    };
  }

  if (
    shortProbability != null &&
    (longProbability == null || shortProbability > longProbability)
  ) {
    return {
      side: "SHORT",
      probability: shortProbability,
    };
  }

  // Equal probabilities have no single higher value.
  return null;
}

const FIRST_PREDICTION_DAY = "2026-02-08";

function gradePrediction(row, todayUtc) {
  const side = row?.historicalPrediction?.prediction;
  const date = fmtDateISO(row?.date);
  if (!date || date < FIRST_PREDICTION_DAY || !["LONG", "SHORT"].includes(side)) return null;
  const gains = [row.longGain, row.shortGain];
  // Score completed days with available results; zero is a valid gain.
  const pending = Date.parse(date) >= todayUtc ||
    gains.some(value => value == null || String(value).trim() === "" || !Number.isFinite(Number(value)));
  if (pending) return { side, correct: null };
  const [long, short] = gains.map(Number);
  return { side, correct: side === "LONG" ? long > 0 || long > short : short > 0 || short > long };
}

function summarizePredictions(rows, todayUtc) {
  const counts = { LONG: 0, SHORT: 0, correctLONG: 0, correctSHORT: 0, pending: 0 };
  for (const row of rows) {
    const grade = gradePrediction(row, todayUtc);
    if (!grade) continue;
    counts[grade.side]++;
    if (grade.correct === null) counts.pending++;
    if (grade.correct) counts["correct" + grade.side]++;
  }
  const scored = counts.LONG + counts.SHORT - counts.pending;
  const correct = counts.correctLONG + counts.correctSHORT;
  return { ...counts, scored, correct, percent: scored ? (100 * correct / scored).toFixed(1) + "%" : "—" };
}

const CATEGORIES = [
  { key: "bullish", label: "Bull" },
  { key: "bearish", label: "Bear" },
  { key: "neutral", label: "Neutral" },
  { key: "reversal", label: "Reversal" },
  { key: "consolidating", label: "Consol." },
  { key: "exhausted", label: "Exhausted" },
];

// clearer, resilient CRCell: shows count, ratio %, and a mini progress bar
function CRCell({ row, catKey }) {
  const counts = row?.prevDayCounts ?? {};
  const ratios = row?.prevDayRatios ?? {};
  const total = Number(counts.total ?? 0);

  const count = Number(counts[catKey] ?? 0);

  // Accept ratio in 0..100 or 0..1; if missing, compute from counts/total.
  let r = ratios[catKey];
  let ratioPct =
    r == null
      ? total > 0
        ? (count / total) * 100
        : null
      : Number(r) <= 1
        ? Number(r) * 100
        : Number(r);

  const ratioText =
    ratioPct == null || Number.isNaN(ratioPct)
      ? "—"
      : `${Math.round(ratioPct)}%`;

  const barWidth =
    ratioPct == null || Number.isNaN(ratioPct)
      ? 0
      : Math.max(0, Math.min(100, ratioPct));

  return (
    <div className="tdh-cr">
      <div className="tdh-cr-row">
        <span className="tdh-chip tdh-chip--slate tdh-cr-chip">
          <strong>C:</strong>&nbsp;{count}
        </span>
        <span className="tdh-chip tdh-chip--light tdh-cr-chip">
          <strong>R:</strong>&nbsp;{ratioText}
        </span>
      </div>
      <div className="tdh-cr-bar" aria-label={`ratio ${ratioText}`}>
        <div className="tdh-cr-fill" style={{ width: `${barWidth}%` }} />
      </div>
    </div>
  );
}

const MpPill = ({ value }) => {
  if (!value) return <span>—</span>;
  const v = String(value).toLowerCase();
  return <span className={`mp-label mp-${v}`}>{titleCase(v)}</span>;
};

const HistoricalProbability = ({ row }) => {
  const result = getHigherHistoricalProbability(row);

  if (!result) {
    return <span>—</span>;
  }

  const sideClass = result.side.toLowerCase();

  return (
    <span className={`mp-label mp-${sideClass}`}>
      {titleCase(sideClass)} {fmtPct(result.probability)}
    </span>
  );
};

const Card = ({ title, right, children }) => (
  <div className="tdh-card">
    <div className="tdh-card__header">
      <h4 className="tdh-card__title">{title}</h4>
      {right ? <div style={{ marginLeft: "auto" }}>{right}</div> : null}
    </div>
    <div className="tdh-card__body">{children}</div>
  </div>
);

const EditorRow = ({ value, onChange }) => {
  const [local, setLocal] = useState(() => ({
    ...value,
    // normalize date input for <input type="date">
    date: value?.date ? fmtDateISO(value.date) : "",
    direction: value?.direction || "",
    rule: value?.rule || "",
    confidence: value?.confidence ?? "",
    longGain: value?.longGain ?? "",
    shortGain: value?.shortGain ?? "",
  }));

  useEffect(() => {
    setLocal({
      ...value,
      date: value?.date ? fmtDateISO(value.date) : "",
      direction: value?.direction || "",
      rule: value?.rule || "",
      confidence: value?.confidence ?? "",
      longGain: value?.longGain ?? "",
      shortGain: value?.shortGain ?? "",
    });
  }, [value]);

  const update = (k, v) => setLocal((p) => ({ ...p, [k]: v }));

  // basic validation
  const confNum = Number(local.confidence);
  const confErr =
    local.confidence === "" ||
    Number.isNaN(confNum) ||
    confNum < 0 ||
    confNum > 100;

  useEffect(() => {
    onChange?.({
      ...local,
      _valid: !confErr && !!local.direction && !!local.date,
      confidence: local.confidence === "" ? "" : Number(local.confidence),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [local]);

  return (
    <div className="tdh-row" style={{ gap: 12, flexWrap: "wrap" }}>
      <label className="tdh-chip tdh-chip--light">
        <div style={{ fontSize: 12, opacity: 0.8 }}>Date</div>
        <input
          type="date"
          value={local.date}
          onChange={(e) => update("date", e.target.value)}
          className="tdh-input"
          style={{ minWidth: 160 }}
        />
      </label>

      <label className="tdh-chip tdh-chip--light">
        <div style={{ fontSize: 12, opacity: 0.8 }}>Direction</div>
        <select
          className="tdh-input"
          value={local.direction}
          onChange={(e) => update("direction", e.target.value)}
          style={{ minWidth: 160 }}
        >
          <option value="">Select…</option>
          {DIRECTIONS.map((d) => (
            <option key={d} value={d}>
              {titleCase(d)}
            </option>
          ))}
        </select>
      </label>

      <label className="tdh-chip tdh-chip--light">
        <div style={{ fontSize: 12, opacity: 0.8 }}>Confidence</div>
        <input
          type="number"
          min={0}
          max={100}
          value={local.confidence}
          onChange={(e) => update("confidence", e.target.value)}
          className={`tdh-input ${confErr ? "tdh-input--error" : ""}`}
          style={{ width: 100 }}
        />
      </label>

      <label className="tdh-chip tdh-chip--light">
        <div style={{ fontSize: 12, opacity: 0.8 }}>Long Gain (%)</div>
        <input
          type="number"
          step="0.01"
          value={local.longGain}
          onChange={(e) => update("longGain", e.target.value)}
          className="tdh-input"
          style={{ width: 120 }}
          placeholder="e.g. 1.25"
        />
      </label>

      <label className="tdh-chip tdh-chip--light">
        <div style={{ fontSize: 12, opacity: 0.8 }}>Short Gain (%)</div>
        <input
          type="number"
          step="0.01"
          value={local.shortGain}
          onChange={(e) => update("shortGain", e.target.value)}
          className="tdh-input"
          style={{ width: 120 }}
          placeholder="e.g. 0.80"
        />
      </label>

      <label className="tdh-chip tdh-chip--light" style={{ flex: 1 }}>
        <div style={{ fontSize: 12, opacity: 0.8 }}>Rule</div>
        <input
          type="text"
          value={local.rule}
          onChange={(e) => update("rule", e.target.value)}
          className="tdh-input"
          placeholder='e.g. "Bear 1"'
        />
      </label>
    </div>
  );
};

function InfoHeader({ label, explanation }) {
  const [open, setOpen] = useState(false);

  return (
    <div
      className="tdh-info-header"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="tdh-info-header__button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        aria-label={`Explain ${label}`}
        aria-expanded={open}
      >
        <span>{label}</span>
        <span className="tdh-info-header__icon">ⓘ</span>
      </button>

      {open && (
        <div
          className="tdh-info-header__tooltip"
          onClick={(e) => e.stopPropagation()}
        >
          {explanation}
        </div>
      )}
    </div>
  );
}

export default function MarketPulseEditor({
  rows,
  fetchUrl,
  saveUrl,
  exportUrl,
  onSave,
  onCreate,
}) {
  const [data, setData] = useState(() => rows || []);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createDraft, setCreateDraft] = useState({
    date: "",
    direction: "",
    confidence: "",
    rule: "",
  });
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "date",
    direction: "desc",
  });

  const [predictionRows, setPredictionRows] = useState(null);
  const [refresh, setRefresh] = useState(0);
  const todayUtc = Date.parse(new Date().toISOString().slice(0, 10));
  const totals = useMemo(() => {
    const source = fetchUrl ? predictionRows : data;
    return source ? summarizePredictions(source, todayUtc) : null;
  }, [fetchUrl, predictionRows, data, todayUtc]);

  const [confidenceInput, setConfidenceInput] = useState("");
  const [minConfidence, setMinConfidence] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [pageLimit, setPageLimit] = useState(100);
  const num = (v) => (v == null || Number.isNaN(Number(v)) ? 0 : Number(v));
  const plClass = (n) => (Number(n) >= 0 ? "tdh-pl-pos" : "tdh-pl-neg");
  const fmtNum = (n) =>
    n == null || Number.isNaN(Number(n)) ? "—" : `${Number(n).toFixed(2)}%`;

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        // toggle ascending/descending
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  // Fetch MarketPulse page
  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!fetchUrl) return;

      setLoading(true);
      setError("");

      try {
        // Safely preserve any existing query parameters on fetchUrl.
        const separator = fetchUrl.includes("?") ? "&" : "?";

        const url =
          `${fetchUrl}${separator}` + `page=${page}&limit=${pageLimit}&predictionStats=1` +
          (minConfidence === "" ? "" : `&minConfidence=${encodeURIComponent(minConfidence)}`);

        const r = await fetch(url);

        if (!r.ok) {
          throw new Error(`Failed to load MarketPulse (${r.status})`);
        }

        const json = await r.json();

        if (cancelled) return;

        setPredictionRows(Array.isArray(json?.predictionRows) ? json.predictionRows : null);
        setData(Array.isArray(json?.items) ? json.items : []);

        setTotalPages(Math.max(1, Number(json?.pages) || 1));

        setTotalRows(Number(json?.total) || 0);
      } catch (e) {
        if (!cancelled) {
          setData([]);
          setPredictionRows(null);
          setTotalRows(0);
          setTotalPages(1);
          setError(e?.message || "Failed to load MarketPulse.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchUrl, page, pageLimit, refresh, minConfidence]);

  // keep in sync if parent updates rows
  useEffect(() => {
    if (rows) setData(rows);
  }, [rows]);

  const withTotals = useMemo(() => {
    return (data || []).map((r) => ({
      ...r,
      totalGain: num(r.longGain) + num(r.shortGain),
    }));
  }, [data]);

  const sorted = useMemo(() => {
    const items = [...withTotals];
    const { key, direction } = sortConfig;

    items.sort((a, b) => {
      let valA, valB;

      if (key === "date") {
        valA = new Date(a.date);
        valB = new Date(b.date);
      } else if (key === "confidence") {
        valA = num(a.confidence);
        valB = num(b.confidence);
      } else if (["direction", "rule"].includes(key)) {
        valA = String(a[key] || "");
        valB = String(b[key] || "");
      } else if (
        [
          "bullish",
          "bearish",
          "neutral",
          "reversal",
          "consolidating",
          "exhausted",
        ].includes(key)
      ) {
        valA = num(a?.prevDayCounts?.[key]);
        valB = num(b?.prevDayCounts?.[key]);
      } else if (key === "longGain") {
        valA = num(a.longGain);
        valB = num(b.longGain);
      } else if (key === "shortGain") {
        valA = num(a.shortGain);
        valB = num(b.shortGain);
      } else if (key === "totalGain") {
        valA = num(a.totalGain);
        valB = num(b.totalGain);
      } else {
        valA = a[key];
        valB = b[key];
      }

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return items;
  }, [withTotals, sortConfig]);

  const beginEdit = (row) => {
    setEditingId(row._id || row.id || fmtDateISO(row.date));
    setDraft({
      ...row,
      date: fmtDateISO(row.date),
      direction: row.direction || "",
      confidence: row.confidence ?? "",
      longGain: row.longGain ?? "",
      shortGain: row.shortGain ?? "",
      rule: row.rule || "",
      _valid: true,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
  };

  const handleSave = async () => {
    if (!draft?._valid) {
      setError(
        "Please fill date & direction and set confidence between 0–100.",
      );
      return;
    }
    setError("");
    const payload = {
      ...draft,
      // ensure schema types
      date: new Date(draft.date).toISOString(),
      confidence: Number(draft.confidence),
      longGain: draft.longGain === "" ? null : Number(draft.longGain),
      shortGain: draft.shortGain === "" ? null : Number(draft.shortGain),
    };

    // optimistic update
    const key = editingId;
    const prev = [...data];
    const next = prev.map((r) => {
      const rid = r._id || r.id || fmtDateISO(r.date);
      return rid === key ? { ...r, ...payload } : r;
    });
    setData(next);

    try {
      if (onSave) {
        await onSave(payload);
      } else if (saveUrl) {
        await fetch(saveUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      setRefresh(v => v + 1);
      cancelEdit();
    } catch (e) {
      setData(prev); // revert
      setError(e?.message || "Save failed.");
    }
  };

  const handleExport = () => {
    if (!exportUrl) return;
    window.open(exportUrl, "_blank");
  };

  const handleCreate = async () => {
    if (
      !createDraft?.date ||
      !createDraft?.direction ||
      createDraft.confidence === ""
    ) {
      setError("Please fill date, direction, confidence (0–100).");
      return;
    }
    const c = Number(createDraft.confidence);
    if (Number.isNaN(c) || c < 0 || c > 100) {
      setError("Confidence must be 0–100.");
      return;
    }
    setError("");

    const payload = {
      ...createDraft,
      date: new Date(createDraft.date).toISOString(),
      confidence: c,
    };

    // optimistic add
    const prev = [...data];
    setData([{ ...payload, _local: true }, ...prev]);
    setCreating(false);
    setCreateDraft({ date: "", direction: "", confidence: "", rule: "" });

    try {
      if (onCreate) {
        const saved = await onCreate(payload);
        if (saved && saved._id) {
          // replace the optimistic row if backend returns an _id
          setData((d) => {
            const copy = [...d];
            const idx = copy.findIndex((r) => r._local);
            if (idx !== -1) copy[idx] = { ...saved };
            return copy;
          });
        }
      } else if (saveUrl) {
        const r = await fetch(saveUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const saved = await r.json().catch(() => null);
        if (saved && saved._id) {
          setData((d) => {
            const copy = [...d];
            const idx = copy.findIndex((r) => r._local);
            if (idx !== -1) copy[idx] = { ...saved };
            return copy;
          });
        }
      }
      setRefresh(v => v + 1);
    } catch (e) {
      setData(prev); // revert
      setError(e?.message || "Create failed.");
    }
  };

  return (
    <div className="tdh">
      <div className="tdh-header">
        <h3 className="tdh-title">Market Pulse — Admin</h3>
      </div>

      <Card
        title="Market Pulse (by Date)"
        right={
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="tdh-tab"
              onClick={handleExport}
              title="Export XLSX"
            >
              Export
            </button>

            <button
              className="tdh-tab"
              onClick={() => setCreating((v) => !v)}
              title="Create new Market Pulse"
            >
              {creating ? "Close" : "New"}
            </button>
          </div>
        }
      >
        {creating && (
          <div className="tdh-divider" style={{ marginBottom: 16 }}>
            <EditorRow
              value={createDraft}
              onChange={(v) => setCreateDraft(v)}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <button className="tdh-tab tdh-tab--buy" onClick={handleCreate}>
                Create
              </button>
              <button className="tdh-tab" onClick={() => setCreating(false)}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {error ? (
          <div
            className="tdh-muted"
            style={{ color: "#ef4444", marginBottom: 8 }}
          >
            {error}
          </div>
        ) : null}

        {fetchUrl && (
          <form style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}
            onSubmit={event => {
              event.preventDefault();
              setMinConfidence(confidenceInput);
              setEditingId(null);
              setDraft(null);
              setPage(1);
            }}>
            <label>Minimum prediction confidence (%)
              <input className="tdh-input" type="number" min="0" max="100" step="any"
                placeholder="Any" value={confidenceInput} style={{ width: 80, marginLeft: 6 }}
                onChange={event => setConfidenceInput(event.target.value)} />
            </label>
            <button type="submit" className="tdh-tab">Apply</button>
            <button type="button" className="tdh-tab" onClick={() => {
              setConfidenceInput("");
              setMinConfidence("");
              setEditingId(null);
              setDraft(null);
              setPage(1);
            }}>Clear</button>
          </form>
        )}

        {loading ? (
          <div className="tdh-muted">Loading…</div>
        ) : (
          <>
            {/* Pagination */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 12,
              }}
            >
              <div
                className="tdh-muted"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
              <span className="tdh-pagination-page">
                {page} / {totalPages}
              </span>

                <span>•</span>

                <span>{totalRows.toLocaleString()} total records</span>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <label
                  className="tdh-muted"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  Rows:
                  <select
                    className="tdh-input"
                    value={pageLimit}
                    onChange={(e) => {
                      setPageLimit(Number(e.target.value));
                      setPage(1);
                    }}
                    style={{
                      width: 80,
                      padding: "6px 8px",
                    }}
                  >
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value={200}>200</option>
                  </select>
                </label>

                <button
                  type="button"
                  className="tdh-tab"
                  disabled={page <= 1 || loading}
                  onClick={() => {
                    setEditingId(null);
                    setDraft(null);
                    setPage((p) => Math.max(1, p - 1));
                  }}
                  style={{
                    opacity: page <= 1 ? 0.5 : 1,
                    cursor: page <= 1 ? "not-allowed" : "pointer",
                  }}
                >
                  ← Previous
                </button>

                <button
                  type="button"
                  className="tdh-tab"
                  disabled={page >= totalPages || loading}
                  onClick={() => {
                    setEditingId(null);
                    setDraft(null);
                    setPage((p) => Math.min(totalPages, p + 1));
                  }}
                  style={{
                    opacity: page >= totalPages ? 0.5 : 1,
                    cursor: page >= totalPages ? "not-allowed" : "pointer",
                  }}
                >
                  Next →
                </button>
              </div>
            </div>

            {totals && (
              <div className="tdh-divider" style={{ marginBottom: 12 }}>
                <strong>Historical predictions since Feb 8, 2026{minConfidence !== "" ? ` · ≥${minConfidence}% confidence` : ""}</strong>
                <div>LONG: {totals.LONG} calls · {totals.correctLONG} correct</div>
                <div>SHORT: {totals.SHORT} calls · {totals.correctSHORT} correct</div>
                <div>Combined success: <strong>{totals.percent}</strong> ({totals.correct}/{totals.scored} scored) · {totals.pending} pending</div>
              </div>
            )}
            <div className="tdh-table-wrap">
              <table className="tdh-table">
                <thead>
                  <tr>
                    <th
                      className="tdh-th clickable"
                      onClick={() => requestSort("date")}
                    >
                      Date{" "}
                      {sortConfig.key === "date" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="tdh-th clickable"
                      onClick={() => requestSort("totalGain")}
                    >
                      Total{" "}
                      {sortConfig.key === "totalGain" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="tdh-th clickable"
                      onClick={() => requestSort("longGain")}
                    >
                      Long{" "}
                      {sortConfig.key === "longGain" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="tdh-th clickable"
                      onClick={() => requestSort("shortGain")}
                    >
                      Short{" "}
                      {sortConfig.key === "shortGain" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="tdh-th clickable"
                      style={{ textAlign: "center" }}
                      onClick={() => requestSort("direction")}
                    >
                      Current{" "}
                      {sortConfig.key === "direction" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th
                      className="tdh-th clickable"
                      style={{ textAlign: "center" }}
                      onClick={() => requestSort("rule")}
                    >
                      Rule{" "}
                      {sortConfig.key === "rule" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>
                    <th className="tdh-th">RSI</th>

                    <th
                      className="tdh-th clickable"
                      onClick={() => requestSort("confidence")}
                    >
                      Confidence{" "}
                      {sortConfig.key === "confidence" &&
                        (sortConfig.direction === "asc" ? "↑" : "↓")}
                    </th>

                    <th className="tdh-th">Future 15D</th>
                    <th className="tdh-th">
                      <InfoHeader
                        label="Historical"
                        explanation="What is expected to happen today based on historically matching Market Pulse conditions and how those matches performed over the following 24 hours (HistoricalPredictionSchema). Future 24 from yesterday has to match today's market pulse direction"
                      />
                    </th>
                    <th className="tdh-th">Hist. Prob.</th>
                    <th className="tdh-th">
                      <InfoHeader
                        label="Future 24"
                        explanation="The predicted market direction for the next 24 hours, based on combinations of Market Pulse conditions (DirectionFutureSchema)."
                      />
                    </th>
                    <th className="tdh-th">Rule 24</th>

                    <th className="tdh-th">Macro</th>
                    <th className="tdh-th">Crypto</th>
                    <th className="tdh-th">Combined</th>
                    <th className="tdh-th">24hr AI</th>
                    <th className="tdh-th tdh-col-hidden">Score</th>

                    {/* 🔽 hide category headers when editing */}
                    {CATEGORIES.map((cat) => (
                      <th
                        key={cat.key}
                        className="tdh-th clickable tdh-col-hidden"
                        onClick={() => requestSort(cat.key)}
                      >
                        {cat.label}{" "}
                        {sortConfig.key === cat.key &&
                          (sortConfig.direction === "asc" ? "↑" : "↓")}
                      </th>
                    ))}
                    <th className="tdh-th" style={{ width: 220 }}>
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sorted.length === 0 ? (
                    <tr>
                      <td className="tdh-td" colSpan={18}>
                        No Market Pulse rows yet.
                      </td>
                    </tr>
                  ) : (
                    sorted.map((row) => {
                      const id = row._id || row.id || fmtDateISO(row.date);
                      const isEditing = editingId === id;
                      const draftTotal =
                        num(draft?.longGain) + num(draft?.shortGain);

                      const historicalEvaluationRow = isEditing
                        ? {
                            ...row,
                            longGain: draft?.longGain,
                            shortGain: draft?.shortGain,
                          }
                        : row;

                      const historicalCorrect = gradePrediction(historicalEvaluationRow, todayUtc)?.correct === true;
                      if (isEditing) {
                        return (
                          <tr
                            key={id}
                            className={
                              historicalCorrect
                                ? "tdh-row--historical-correct"
                                : undefined
                            }
                          >
                            <td className="tdh-td" style={{ minWidth: 140 }}>
                              <input
                                type="date"
                                className="tdh-input"
                                value={draft?.date || ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    date: e.target.value,
                                  }))
                                }
                              />
                            </td>
                            <td className={`tdh-td ${plClass(draftTotal)}`}>
                              {fmtNum(draftTotal)}
                            </td>
                            <td className="tdh-td" style={{ width: 120 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="tdh-input"
                                value={draft?.longGain ?? ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    longGain: e.target.value,
                                  }))
                                }
                                placeholder="e.g. 1.25"
                              />
                            </td>
                            <td className="tdh-td" style={{ width: 120 }}>
                              <input
                                type="number"
                                step="0.01"
                                className="tdh-input"
                                value={draft?.shortGain ?? ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    shortGain: e.target.value,
                                  }))
                                }
                                placeholder="e.g. 0.80"
                              />
                            </td>
                            <td className="tdh-td" style={{ minWidth: 160 }}>
                              <select
                                className="tdh-input"
                                value={draft?.direction || ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    direction: e.target.value,
                                  }))
                                }
                              >
                                <option value="">Select…</option>
                                {DIRECTIONS.map((d) => (
                                  <option key={d} value={d}>
                                    {titleCase(d)}
                                  </option>
                                ))}
                              </select>
                            </td>

                            <td className="tdh-td" style={{ width: 120 }}>
                              <input
                                type="number"
                                className="tdh-input"
                                min={0}
                                max={100}
                                value={draft?.total3Rsi ?? ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    total3Rsi: e.target.value,
                                  }))
                                }
                              />
                            </td>

                            <td className="tdh-td" style={{ width: 120 }}>
                              <input
                                type="number"
                                className="tdh-input"
                                min={0}
                                max={100}
                                value={draft?.confidence ?? ""}
                                onChange={(e) =>
                                  setDraft((p) => ({
                                    ...p,
                                    confidence: e.target.value,
                                  }))
                                }
                              />
                            </td>

                            <td className="tdh-td">
                              <MpPill value={row.aiMacroPulse} />
                            </td>

                            <td className="tdh-td">
                              <MpPill
                                value={row.historicalPrediction?.prediction}
                              />
                            </td>

                            <td className="tdh-td">
                              <HistoricalProbability row={row} />
                            </td>

                            <td className="tdh-td">
                              <MpPill
                                value={row.directionFuture?.predictedDirection}
                              />
                            </td>

                            <td className="tdh-td">
                              {row.directionFuture?.marketPulseRulePrediction ||
                                "—"}
                            </td>
                            {/* 🔽 hide category cells when editing */}
                            {!isEditing &&
                              CATEGORIES.map((cat) => (
                                <td
                                  key={cat.key}
                                  className="tdh-td"
                                  style={{ minWidth: 120 }}
                                >
                                  <CRCell row={row} catKey={cat.key} />
                                </td>
                              ))}

                            <td className="tdh-td">
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  className="tdh-tab tdh-tab--buy"
                                  onClick={handleSave}
                                >
                                  Save
                                </button>
                                <button
                                  className="tdh-tab"
                                  onClick={cancelEdit}
                                >
                                  Cancel
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      }

                      return (
                        <tr
                          key={id}
                          className={
                            historicalCorrect
                              ? "tdh-row--historical-correct"
                              : undefined
                          }
                        >
                          <td className="tdh-td">{fmtDateISO(row.date)}</td>
                          <td className={`tdh-td ${plClass(row.totalGain)}`}>
                            {fmtNum(row.totalGain)}
                          </td>
                          <td className={`tdh-td ${plClass(row.longGain)}`}>
                            {fmtNum(row.longGain)}
                          </td>
                          <td className={`tdh-td ${plClass(row.shortGain)}`}>
                            {fmtNum(row.shortGain)}
                          </td>
                          <td className="tdh-td">
                            <MpPill value={row.direction} />
                          </td>
                          <td className="tdh-td">
                            {row.rule ? (
                              <span
                                className={`mp-label mp-${String(row.rule).toLowerCase()}`}
                              >
                                {row.rule}
                              </span>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="tdh-td">
                            {row.total3Rsi == null
                              ? "—"
                              : fmtPct(row.total3Rsi)}
                          </td>

                          <td className="tdh-td">
                            {row.confidence == null
                              ? "—"
                              : fmtPct(row.confidence)}
                          </td>

                          <td className="tdh-td">
                            <MpPill value={row.aiMacroPulse} />
                          </td>

                          <td className="tdh-td">
                            <MpPill
                              value={row.historicalPrediction?.prediction}
                            />
                          </td>

                          <td className="tdh-td">
                            <HistoricalProbability row={row} />
                          </td>

                          <td className="tdh-td">
                            <MpPill
                              value={row.directionFuture?.predictedDirection}
                            />
                          </td>

                          <td className="tdh-td">
                            {row.directionFuture?.marketPulseRulePrediction ||
                              "—"}
                          </td>

                          {(() => {
                            const regimes = row.regimes || [];
                            const last = regimes.length
                              ? regimes[regimes.length - 1]
                              : null;

                            return (
                              <>
                                <td className="tdh-td">
                                  {last?.macroRegime || "—"}
                                </td>
                                <td className="tdh-td">
                                  {last?.cryptoRegime || "—"}
                                </td>
                                <td className="tdh-td">
                                  {last?.combinedDirection || "—"}
                                </td>
                                <td className="tdh-td">
                                  <MpPill value={row.macroRegimeAiDirection} />
                                </td>
                                <td className="tdh-td tdh-col-hidden">
                                  {typeof last?.score === "number"
                                    ? last.score.toFixed(1)
                                    : "—"}
                                </td>
                              </>
                            );
                          })()}

                          {CATEGORIES.map((cat) => (
                            <td
                              key={cat.key}
                              className="tdh-td tdh-col-hidden"
                              style={{ maxWidth: 100 }}
                            >
                              <CRCell row={row} catKey={cat.key} />
                            </td>
                          ))}

                          <td className="tdh-td">
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                flexWrap: "wrap",
                              }}
                            >
                              <button
                                className="tdh-tab"
                                onClick={() => beginEdit(row)}
                              >
                                Edit
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
